import { CallExpressionShape, Plugin, ts } from '@servicenow/sdk-build-core'
import type { Diagnostics } from '@servicenow/sdk-build-core'
import {
    ACTIVITY_CALLEE_NAMES,
    DATAPILL_CALLEE,
    LANE_CALLEE_NAMES,
    PLAYBOOK_API_NAME,
    TRIGGER_INSTANCE_CALLEE_NAMES,
} from '../constants/playbook-plugin-constants'

/** Config field paths where dataPill() is allowed inside an Activity config (arg 1). */
const ACTIVITY_CONFIG_PILL_PATHS = new Set([
    'conditionToRun',
    'startWithDelay.duration',
    'startWithDelay.relativeDatetime',
    'startWithDelay.percentage',
    'startWithDelay.percentageDatetime',
])

/** Config field paths where dataPill() is allowed inside a Lane config (arg 0). */
const LANE_CONFIG_PILL_PATHS = new Set([
    'conditionToRun',
    'startWithDelay.duration',
    'startWithDelay.relativeDatetime',
    'startWithDelay.percentage',
    'startWithDelay.percentageDatetime',
])

/** Trigger instance callee names as a Set for lookup. */
const TRIGGER_CALLEE_NAMES = new Set(TRIGGER_INSTANCE_CALLEE_NAMES)

/**
 * Get the callee text of a CallExpression node (e.g. 'Activity', 'wfa.playbook.lane').
 */
function getCalleeName(node: ts.CallExpression): string | undefined {
    const expr = node.getExpression()
    if (ts.Node.isIdentifier(expr) || ts.Node.isPropertyAccessExpression(expr)) {
        return expr.getText()
    }
    return undefined
}

/**
 * Check whether `node` is the same as or a descendant of `ancestor`.
 */
function isDescendantOrSelf(node: ts.Node, ancestor: ts.Node): boolean {
    let current: ts.Node | undefined = node
    while (current) {
        if (current === ancestor) {
            return true
        }
        current = current.getParent()
    }
    return false
}

/**
 * Walk up from a node to find the nearest ancestor CallExpression whose callee
 * matches one of the given names. Returns both the call and the argument index
 * that contains the starting node.
 */
function findAncestorCallWithArgIndex(
    node: ts.Node,
    calleeNames: Set<string>
): { call: ts.CallExpression; argIndex: number } | undefined {
    let current: ts.Node | undefined = node.getParent()

    while (current) {
        if (ts.Node.isCallExpression(current)) {
            const callee = getCalleeName(current)
            if (callee && calleeNames.has(callee)) {
                const args = current.getArguments()
                for (let i = 0; i < args.length; i++) {
                    const arg = args[i]
                    if (arg && isDescendantOrSelf(node, arg)) {
                        return { call: current, argIndex: i }
                    }
                }
                return { call: current, argIndex: -1 }
            }
        }
        current = current.getParent()
    }
    return undefined
}

/**
 * Build the property path from a node up to a config argument.
 *
 * Walks up from `node` collecting PropertyAssignment names until reaching
 * `configArg`, then joins them with dots.
 *
 * Example: for `startWithDelay: { relativeDatetime: dataPill(...) }`,
 * returns `'startWithDelay.relativeDatetime'`.
 */
function buildConfigPropertyPath(node: ts.Node, configArg: ts.Node): string {
    const parts: string[] = []
    let current: ts.Node | undefined = node.getParent()
    while (current && current !== configArg) {
        if (ts.Node.isPropertyAssignment(current)) {
            parts.unshift(current.getName())
        }
        current = current.getParent()
    }
    return parts.join('.')
}

/**
 * Validate that a dataPill call is in an allowed position within the playbook tree.
 */
function validateDataPillPosition(pillNode: ts.CallExpression, diagnostics: Diagnostics): void {
    // Check for Activity ancestor first (most specific context)
    const activityAncestor = findAncestorCallWithArgIndex(pillNode, ACTIVITY_CALLEE_NAMES)
    if (activityAncestor) {
        const { argIndex, call } = activityAncestor
        // arg 0 = activity definition, arg 1 = config, arg 2 = inputs, arg 3 = experience
        if (argIndex === 2 || argIndex === 3) {
            return
        }
        if (argIndex === 1) {
            const configArg = call.getArguments()[1]
            if (configArg) {
                const path = buildConfigPropertyPath(pillNode, configArg)
                if (ACTIVITY_CONFIG_PILL_PATHS.has(path)) {
                    return
                }
            }
            diagnostics.error(
                pillNode,
                'wfa.playbook.dataPill() is not allowed in activity config fields. ' +
                    'Pills can only be used in inputs, experience properties, conditionToRun, or startWithDelay timer fields.'
            )
            return
        }
        diagnostics.error(pillNode, 'wfa.playbook.dataPill() is not allowed in this position within an Activity.')
        return
    }

    // Check for Lane ancestor
    const laneAncestor = findAncestorCallWithArgIndex(pillNode, LANE_CALLEE_NAMES)
    if (laneAncestor) {
        const { argIndex, call } = laneAncestor
        if (argIndex === 0) {
            const configArg = call.getArguments()[0]
            if (configArg) {
                const path = buildConfigPropertyPath(pillNode, configArg)
                if (LANE_CONFIG_PILL_PATHS.has(path)) {
                    return
                }
            }
            diagnostics.error(
                pillNode,
                'wfa.playbook.dataPill() is not allowed in lane config fields. ' +
                    'Pills can only be used in conditionToRun or startWithDelay timer fields.'
            )
            return
        }
        diagnostics.error(pillNode, 'wfa.playbook.dataPill() is not allowed in this position within a Lane.')
        return
    }

    // Check for PlaybookTrigger ancestor
    const triggerAncestor = findAncestorCallWithArgIndex(pillNode, TRIGGER_CALLEE_NAMES)
    if (triggerAncestor) {
        const { argIndex } = triggerAncestor
        // arg 0 = trigger type, arg 1 = config, arg 2 = trigger inputs, arg 3 = playbook inputs mapper
        if (argIndex === 3) {
            // Pills are allowed in the mapper callback (4th argument)
            return
        }
        diagnostics.error(
            pillNode,
            'wfa.playbook.dataPill() is not allowed in this position within a PlaybookTrigger. ' +
                'Pills can only be used in the playbook inputs mapper (4th argument).'
        )
        return
    }

    // Not inside any Activity, Lane, or Trigger
    diagnostics.error(
        pillNode,
        'wfa.playbook.dataPill() is not allowed in PlaybookDefinition config or declarations. ' +
            'Pills can only be used inside Activity inputs, experience properties, condition fields, or trigger mappers.'
    )
}

/**
 * Diagnostics-only plugin that validates dataPill usage across the entire
 * PlaybookDefinition tree.
 *
 * Invoked from PlaybookDefinitionPlugin before record creation. Walks the
 * root AST node to find all wfa.playbook.dataPill() calls and validates
 * that each one is in a position that supports pill references.
 */
export const PlaybookDataPillDiagnosticsPlugin = Plugin.create({
    name: 'PlaybookDataPillDiagnosticsPlugin',
    shapes: [
        {
            shape: CallExpressionShape,
            fileTypes: ['fluent'],

            toRecord(callExpression, { diagnostics }) {
                if (callExpression.getCallee() !== PLAYBOOK_API_NAME) {
                    return { success: false }
                }

                const rootNode = callExpression.getOriginalNode()
                if (!ts.Node.isCallExpression(rootNode)) {
                    return { success: false }
                }

                const dataPillCalls = rootNode
                    .getDescendantsOfKind(ts.SyntaxKind.CallExpression)
                    .filter((node) => getCalleeName(node) === DATAPILL_CALLEE)

                for (const pillCall of dataPillCalls) {
                    validateDataPillPosition(pillCall, diagnostics)
                }

                return { success: false }
            },
        },
    ],
    docs: [],
})
